/**
 * Shape types supported by the shape tool
 */
export type ShapeType = 'rectangle' | 'ellipse' | 'polygon' | 'star' | 'arrow' | 'cross' | 'diamond' | 'callout';

/**
 * Corner style for rounded corners
 */
export type CornerStyle = 'round' | 'chamfer' | 'invert';

/**
 * Arrow style variants
 */
export type ArrowStyle = 'block' | 'chevron' | 'pointer' | 'double' | 'curved';

/**
 * Callout pointer position
 */
export type CalloutPointerPosition = 'top' | 'right' | 'bottom' | 'left' | 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';

/**
 * Corner radius configuration for rectangles
 * Supports individual corner values or uniform mode
 */
export interface CornerRadius {
  /** Top-left corner radius */
  topLeft: number;
  /** Top-right corner radius */
  topRight: number;
  /** Bottom-right corner radius */
  bottomRight: number;
  /** Bottom-left corner radius */
  bottomLeft: number;
  /** Corner style (round, chamfer, or invert) */
  style: CornerStyle;
  /** When true, all corners use topLeft value */
  uniform: boolean;
}

/**
 * Default corner radius
 */
export const DEFAULT_CORNER_RADIUS: CornerRadius = {
  topLeft: 0,
  topRight: 0,
  bottomRight: 0,
  bottomLeft: 0,
  style: 'round',
  uniform: true
};

/**
 * Rectangle shape parameters
 */
export interface RectangleParams {
  /** Width of the rectangle */
  width: number;
  /** Height of the rectangle */
  height: number;
  /** Corner radius configuration */
  cornerRadius: CornerRadius;
}

/**
 * Ellipse shape parameters
 */
export interface EllipseParams {
  /** Horizontal radius */
  radiusX: number;
  /** Vertical radius */
  radiusY: number;
}

/**
 * Regular polygon shape parameters
 */
export interface PolygonParams {
  /** Number of sides (3-60) */
  sides: number;
  /** Outer radius */
  radius: number;
  /** Corner radius for rounding */
  cornerRadius: number;
}

/**
 * Star shape parameters
 */
export interface StarParams {
  /** Number of points (3-60) */
  points: number;
  /** Outer radius (tip of points) */
  outerRadius: number;
  /** Ratio of inner radius to outer radius (0.1-0.9) */
  innerRatio: number;
  /** Corner radius for rounding */
  cornerRadius: number;
}

/**
 * Arrow shape parameters
 */
export interface ArrowParams {
  /** Arrow style variant */
  style: ArrowStyle;
  /** Total width */
  width: number;
  /** Total height */
  height: number;
  /** Ratio of head width to total width (0.3-0.8) */
  headRatio: number;
  /** Ratio of shaft width to head width (0.2-0.8) */
  shaftRatio: number;
}

/**
 * Cross/plus shape parameters
 */
export interface CrossParams {
  /** Total width */
  width: number;
  /** Total height */
  height: number;
  /** Ratio of arm width to dimension (0.1-0.5) */
  armRatio: number;
}

/**
 * Diamond shape parameters
 */
export interface DiamondParams {
  /** Width of the diamond */
  width: number;
  /** Height of the diamond */
  height: number;
}

/**
 * Callout/speech bubble shape parameters
 */
export interface CalloutParams {
  /** Width of the callout body */
  width: number;
  /** Height of the callout body */
  height: number;
  /** Corner radius of the body */
  cornerRadius: number;
  /** Position of the pointer */
  pointerPosition: CalloutPointerPosition;
  /** Width of the pointer base */
  pointerWidth: number;
  /** Length of the pointer */
  pointerLength: number;
  /** Offset along the edge (-0.5 to 0.5) */
  pointerOffset: number;
}

/**
 * Union type for all shape parameters
 */
export type ShapeParams =
  | { type: 'rectangle'; params: RectangleParams }
  | { type: 'ellipse'; params: EllipseParams }
  | { type: 'polygon'; params: PolygonParams }
  | { type: 'star'; params: StarParams }
  | { type: 'arrow'; params: ArrowParams }
  | { type: 'cross'; params: CrossParams }
  | { type: 'diamond'; params: DiamondParams }
  | { type: 'callout'; params: CalloutParams };

// Import stroke types from penPath for consistency
import { LineCap, LineJoin, DashPreset } from './penPath';

/**
 * Shape instance placed on the map
 */
export interface ShapeInstance {
  /** Unique identifier */
  id: string;
  /** Type of shape */
  shapeType: ShapeType;
  /** Shape-specific parameters */
  shapeParams: ShapeParams;

  // Position and transformation
  /** X position of shape center in world coordinates */
  x: number;
  /** Y position of shape center in world coordinates */
  y: number;
  /** Rotation in degrees */
  rotation: number;
  /** Uniform scale factor */
  scale: number;

  // Stroke style (same as PenPathInstance)
  /** Stroke color in hex format, null for no stroke */
  strokeColor: string | null;
  /** Stroke width in pixels */
  strokeWidth: number;
  /** Stroke opacity (0-1) */
  strokeOpacity: number;
  /** Line cap style */
  lineCap: LineCap;
  /** Line join style */
  lineJoin: LineJoin;
  /** Dash pattern preset */
  dashPreset: DashPreset;

  // Fill style
  /** Fill color in hex format, null for no fill */
  fillColor: string | null;
  /** Fill opacity (0-1) */
  fillOpacity: number;
  /** Feather radius for soft edges on fill (0 = sharp edges) */
  featherRadius: number;

  // Metadata
  /** Z-index for rendering order */
  zIndex: number;
  /** Layer ID this shape belongs to */
  layerId: string;
  /** Region X coordinate (calculated from position) */
  regionX?: number;
  /** Region Y coordinate (calculated from position) */
  regionY?: number;
}

/**
 * Settings for the shape tool
 */
export interface ShapeToolSettings {
  /** Currently selected shape type */
  selectedShapeType: ShapeType;

  // Stroke style (same as PenToolSettings)
  /** Default stroke color */
  strokeColor: string;
  /** Default stroke width */
  strokeWidth: number;
  /** Default stroke opacity */
  strokeOpacity: number;
  /** Default line cap */
  lineCap: LineCap;
  /** Default line join */
  lineJoin: LineJoin;
  /** Default dash preset */
  dashPreset: DashPreset;

  // Fill style
  /** Default fill color */
  fillColor: string | null;
  /** Default fill opacity */
  fillOpacity: number;
  /** Default feather radius for soft edges (0 = sharp edges) */
  featherRadius: number;

  // Shape-specific defaults
  /** Default corner radius for rectangles */
  rectangleCornerRadius: number;
  /** Default number of sides for polygons */
  polygonSides: number;
  /** Default number of points for stars */
  starPoints: number;
  /** Default inner ratio for stars */
  starInnerRatio: number;
  /** Default arrow style */
  arrowStyle: ArrowStyle;
  /** Default callout pointer position */
  calloutPointerPosition: CalloutPointerPosition;
}

/**
 * Default shape tool settings
 */
export const DEFAULT_SHAPE_TOOL_SETTINGS: ShapeToolSettings = {
  selectedShapeType: 'rectangle',
  strokeColor: '#000000',
  strokeWidth: 2,
  strokeOpacity: 1,
  lineCap: 'round',
  lineJoin: 'round',
  dashPreset: 'solid',
  fillColor: '#3388ff',
  fillOpacity: 0.5,
  featherRadius: 0,
  rectangleCornerRadius: 0,
  polygonSides: 6,
  starPoints: 5,
  starInnerRatio: 0.5,
  arrowStyle: 'block',
  calloutPointerPosition: 'bottom'
};

/**
 * Region data for shapes
 */
export interface RegionShapes {
  /** Region X coordinate */
  regionX: number;
  /** Region Y coordinate */
  regionY: number;
  /** List of shapes in this region */
  shapes: ShapeInstance[];
}

// ============================================================================
// PATH GENERATION ALGORITHMS
// ============================================================================

/**
 * Simple 2D point
 */
export interface Point {
  x: number;
  y: number;
}

/**
 * Path command for drawing
 * Supports moveTo, lineTo, bezierCurveTo, and closePath
 */
export type PathCommand =
  | { type: 'moveTo'; x: number; y: number }
  | { type: 'lineTo'; x: number; y: number }
  | { type: 'bezierCurveTo'; cp1x: number; cp1y: number; cp2x: number; cp2y: number; x: number; y: number }
  | { type: 'quadraticCurveTo'; cpx: number; cpy: number; x: number; y: number }
  | { type: 'closePath' };

/**
 * Kappa constant for Bezier circle approximation
 * 4/3 * tan(PI/8) = 0.5522847498...
 */
const KAPPA = 0.5522847498307936;

/**
 * Generates path commands for any shape type
 * @param shape The shape instance
 * @returns Array of path commands
 */
export function generateShapePath(shape: ShapeInstance): PathCommand[] {
  switch (shape.shapeType) {
    case 'rectangle':
      return generateRectanglePath(shape);
    case 'ellipse':
      return generateEllipsePath(shape);
    case 'polygon':
      return generatePolygonPath(shape);
    case 'star':
      return generateStarPath(shape);
    case 'arrow':
      return generateArrowPath(shape);
    case 'cross':
      return generateCrossPath(shape);
    case 'diamond':
      return generateDiamondPath(shape);
    case 'callout':
      return generateCalloutPath(shape);
    default:
      return [];
  }
}

/**
 * Generates rectangle path with optional rounded corners
 * @param shape Shape instance with rectangle params
 * @returns Path commands
 */
function generateRectanglePath(shape: ShapeInstance): PathCommand[] {
  const params = shape.shapeParams.params as RectangleParams;
  const { width, height, cornerRadius } = params;
  const hw = width / 2;
  const hh = height / 2;
  const maxR = Math.min(hw, hh);

  // Get corner radii (clamped to max)
  const getRadius = (value: number): number => Math.min(Math.max(0, value), maxR);
  const tl = getRadius(cornerRadius.uniform ? cornerRadius.topLeft : cornerRadius.topLeft);
  const tr = getRadius(cornerRadius.uniform ? cornerRadius.topLeft : cornerRadius.topRight);
  const br = getRadius(cornerRadius.uniform ? cornerRadius.topLeft : cornerRadius.bottomRight);
  const bl = getRadius(cornerRadius.uniform ? cornerRadius.topLeft : cornerRadius.bottomLeft);

  const commands: PathCommand[] = [];
  const cx = shape.x;
  const cy = shape.y;

  if (tl === 0 && tr === 0 && br === 0 && bl === 0) {
    // Simple rectangle without rounded corners
    commands.push({ type: 'moveTo', x: cx - hw, y: cy - hh });
    commands.push({ type: 'lineTo', x: cx + hw, y: cy - hh });
    commands.push({ type: 'lineTo', x: cx + hw, y: cy + hh });
    commands.push({ type: 'lineTo', x: cx - hw, y: cy + hh });
    commands.push({ type: 'closePath' });
  } else {
    // Rectangle with rounded corners
    const style = cornerRadius.style;

    // Start at top-left after corner
    commands.push({ type: 'moveTo', x: cx - hw + tl, y: cy - hh });

    // Top edge and top-right corner
    commands.push({ type: 'lineTo', x: cx + hw - tr, y: cy - hh });
    if (tr > 0) {
      addCorner(commands, cx + hw - tr, cy - hh, tr, -Math.PI / 2, 0, style);
    }

    // Right edge and bottom-right corner
    commands.push({ type: 'lineTo', x: cx + hw, y: cy + hh - br });
    if (br > 0) {
      addCorner(commands, cx + hw - br, cy + hh - br, br, 0, Math.PI / 2, style);
    }

    // Bottom edge and bottom-left corner
    commands.push({ type: 'lineTo', x: cx - hw + bl, y: cy + hh });
    if (bl > 0) {
      addCorner(commands, cx - hw + bl, cy + hh - bl, bl, Math.PI / 2, Math.PI, style);
    }

    // Left edge and top-left corner
    commands.push({ type: 'lineTo', x: cx - hw, y: cy - hh + tl });
    if (tl > 0) {
      addCorner(commands, cx - hw + tl, cy - hh + tl, tl, Math.PI, Math.PI * 1.5, style);
    }

    commands.push({ type: 'closePath' });
  }

  return commands;
}

/**
 * Adds a corner arc to the path commands
 * @param commands Path commands array to append to
 * @param cx Corner center X
 * @param cy Corner center Y
 * @param r Corner radius
 * @param startAngle Start angle in radians
 * @param endAngle End angle in radians
 * @param style Corner style
 */
function addCorner(
  commands: PathCommand[],
  cx: number,
  cy: number,
  r: number,
  startAngle: number,
  endAngle: number,
  style: CornerStyle
): void {
  if (style === 'chamfer') {
    // Simple straight line (chamfer)
    const endX = cx + r * Math.cos(endAngle);
    const endY = cy + r * Math.sin(endAngle);
    commands.push({ type: 'lineTo', x: endX, y: endY });
  } else if (style === 'invert') {
    // Inverted arc (curves inward)
    const startX = cx + r * Math.cos(startAngle);
    const startY = cy + r * Math.sin(startAngle);
    const endX = cx + r * Math.cos(endAngle);
    const endY = cy + r * Math.sin(endAngle);

    // Control points for inverted Bezier curve
    const midAngle = (startAngle + endAngle) / 2;
    const invertRadius = r * 0.5; // How far inward
    const cpX = cx + invertRadius * Math.cos(midAngle);
    const cpY = cy + invertRadius * Math.sin(midAngle);

    commands.push({
      type: 'quadraticCurveTo',
      cpx: cpX,
      cpy: cpY,
      x: endX,
      y: endY
    });
  } else {
    // Round corner using Bezier approximation
    const k = KAPPA * r;
    const startX = cx + r * Math.cos(startAngle);
    const startY = cy + r * Math.sin(startAngle);
    const endX = cx + r * Math.cos(endAngle);
    const endY = cy + r * Math.sin(endAngle);

    // Tangent directions
    const cp1x = startX - k * Math.sin(startAngle);
    const cp1y = startY + k * Math.cos(startAngle);
    const cp2x = endX + k * Math.sin(endAngle);
    const cp2y = endY - k * Math.cos(endAngle);

    commands.push({
      type: 'bezierCurveTo',
      cp1x, cp1y,
      cp2x, cp2y,
      x: endX,
      y: endY
    });
  }
}

/**
 * Generates ellipse path using Bezier curves
 * @param shape Shape instance with ellipse params
 * @returns Path commands
 */
function generateEllipsePath(shape: ShapeInstance): PathCommand[] {
  const params = shape.shapeParams.params as EllipseParams;
  const { radiusX, radiusY } = params;
  const cx = shape.x;
  const cy = shape.y;

  const kx = KAPPA * radiusX;
  const ky = KAPPA * radiusY;

  const commands: PathCommand[] = [];

  // Start at top
  commands.push({ type: 'moveTo', x: cx, y: cy - radiusY });

  // Top to right
  commands.push({
    type: 'bezierCurveTo',
    cp1x: cx + kx, cp1y: cy - radiusY,
    cp2x: cx + radiusX, cp2y: cy - ky,
    x: cx + radiusX, y: cy
  });

  // Right to bottom
  commands.push({
    type: 'bezierCurveTo',
    cp1x: cx + radiusX, cp1y: cy + ky,
    cp2x: cx + kx, cp2y: cy + radiusY,
    x: cx, y: cy + radiusY
  });

  // Bottom to left
  commands.push({
    type: 'bezierCurveTo',
    cp1x: cx - kx, cp1y: cy + radiusY,
    cp2x: cx - radiusX, cp2y: cy + ky,
    x: cx - radiusX, y: cy
  });

  // Left to top
  commands.push({
    type: 'bezierCurveTo',
    cp1x: cx - radiusX, cp1y: cy - ky,
    cp2x: cx - kx, cp2y: cy - radiusY,
    x: cx, y: cy - radiusY
  });

  commands.push({ type: 'closePath' });

  return commands;
}

/**
 * Generates regular polygon path
 * @param shape Shape instance with polygon params
 * @returns Path commands
 */
function generatePolygonPath(shape: ShapeInstance): PathCommand[] {
  const params = shape.shapeParams.params as PolygonParams;
  const { sides, radius, cornerRadius } = params;
  const cx = shape.x;
  const cy = shape.y;

  const angleStep = (2 * Math.PI) / sides;
  const startAngle = -Math.PI / 2; // Start at top

  // Generate vertices
  const vertices: Point[] = [];
  for (let i = 0; i < sides; i++) {
    const angle = startAngle + i * angleStep;
    vertices.push({
      x: cx + radius * Math.cos(angle),
      y: cy + radius * Math.sin(angle)
    });
  }

  if (cornerRadius > 0) {
    return generateRoundedPolygonPath(vertices, cornerRadius, true);
  }

  // Simple polygon without rounding
  const commands: PathCommand[] = [];
  commands.push({ type: 'moveTo', x: vertices[0].x, y: vertices[0].y });
  for (let i = 1; i < vertices.length; i++) {
    commands.push({ type: 'lineTo', x: vertices[i].x, y: vertices[i].y });
  }
  commands.push({ type: 'closePath' });

  return commands;
}

/**
 * Generates star path
 * @param shape Shape instance with star params
 * @returns Path commands
 */
function generateStarPath(shape: ShapeInstance): PathCommand[] {
  const params = shape.shapeParams.params as StarParams;
  const { points, outerRadius, innerRatio, cornerRadius } = params;
  const cx = shape.x;
  const cy = shape.y;

  const innerRadius = outerRadius * Math.max(0.1, Math.min(0.9, innerRatio));
  const totalPoints = points * 2;
  const angleStep = Math.PI / points;
  const startAngle = -Math.PI / 2; // Start at top

  // Generate vertices alternating outer/inner
  const vertices: Point[] = [];
  for (let i = 0; i < totalPoints; i++) {
    const angle = startAngle + i * angleStep;
    const r = i % 2 === 0 ? outerRadius : innerRadius;
    vertices.push({
      x: cx + r * Math.cos(angle),
      y: cy + r * Math.sin(angle)
    });
  }

  if (cornerRadius > 0) {
    return generateRoundedPolygonPath(vertices, cornerRadius, true);
  }

  // Simple star without rounding
  const commands: PathCommand[] = [];
  commands.push({ type: 'moveTo', x: vertices[0].x, y: vertices[0].y });
  for (let i = 1; i < vertices.length; i++) {
    commands.push({ type: 'lineTo', x: vertices[i].x, y: vertices[i].y });
  }
  commands.push({ type: 'closePath' });

  return commands;
}

/**
 * Generates rounded polygon path from vertices
 * @param vertices Array of polygon vertices
 * @param cornerRadius Radius for rounding corners
 * @param closed Whether the polygon is closed
 * @returns Path commands
 */
function generateRoundedPolygonPath(
  vertices: Point[],
  cornerRadius: number,
  closed: boolean
): PathCommand[] {
  const commands: PathCommand[] = [];
  const n = vertices.length;

  if (n < 3) return commands;

  for (let i = 0; i < n; i++) {
    const curr = vertices[i];
    const prev = vertices[(i - 1 + n) % n];
    const next = vertices[(i + 1) % n];

    // Vectors to prev and next
    const toPrev = { x: prev.x - curr.x, y: prev.y - curr.y };
    const toNext = { x: next.x - curr.x, y: next.y - curr.y };

    // Distances
    const distPrev = Math.sqrt(toPrev.x * toPrev.x + toPrev.y * toPrev.y);
    const distNext = Math.sqrt(toNext.x * toNext.x + toNext.y * toNext.y);

    // Limit corner radius to half the shorter edge
    const maxRadius = Math.min(distPrev, distNext) / 2;
    const r = Math.min(cornerRadius, maxRadius);

    if (r <= 0) {
      if (i === 0) {
        commands.push({ type: 'moveTo', x: curr.x, y: curr.y });
      } else {
        commands.push({ type: 'lineTo', x: curr.x, y: curr.y });
      }
      continue;
    }

    // Normalize vectors
    const prevNorm = { x: toPrev.x / distPrev, y: toPrev.y / distPrev };
    const nextNorm = { x: toNext.x / distNext, y: toNext.y / distNext };

    // Points where rounding starts/ends
    const startPt = { x: curr.x + prevNorm.x * r, y: curr.y + prevNorm.y * r };
    const endPt = { x: curr.x + nextNorm.x * r, y: curr.y + nextNorm.y * r };

    if (i === 0) {
      commands.push({ type: 'moveTo', x: startPt.x, y: startPt.y });
    } else {
      commands.push({ type: 'lineTo', x: startPt.x, y: startPt.y });
    }

    // Quadratic curve through the corner
    commands.push({
      type: 'quadraticCurveTo',
      cpx: curr.x,
      cpy: curr.y,
      x: endPt.x,
      y: endPt.y
    });
  }

  if (closed) {
    commands.push({ type: 'closePath' });
  }

  return commands;
}

/**
 * Generates arrow path
 * @param shape Shape instance with arrow params
 * @returns Path commands
 */
function generateArrowPath(shape: ShapeInstance): PathCommand[] {
  const params = shape.shapeParams.params as ArrowParams;
  const { style, width, height, headRatio, shaftRatio } = params;
  const cx = shape.x;
  const cy = shape.y;

  const hw = width / 2;
  const hh = height / 2;
  const headWidth = width * headRatio;
  const shaftWidth = headWidth * shaftRatio;
  const shaftHalfWidth = shaftWidth / 2;
  const headStart = hw - headWidth;

  const commands: PathCommand[] = [];

  switch (style) {
    case 'block':
    default:
      // Classic block arrow pointing right
      commands.push({ type: 'moveTo', x: cx - hw, y: cy - shaftHalfWidth });
      commands.push({ type: 'lineTo', x: cx + headStart, y: cy - shaftHalfWidth });
      commands.push({ type: 'lineTo', x: cx + headStart, y: cy - hh });
      commands.push({ type: 'lineTo', x: cx + hw, y: cy });
      commands.push({ type: 'lineTo', x: cx + headStart, y: cy + hh });
      commands.push({ type: 'lineTo', x: cx + headStart, y: cy + shaftHalfWidth });
      commands.push({ type: 'lineTo', x: cx - hw, y: cy + shaftHalfWidth });
      commands.push({ type: 'closePath' });
      break;

    case 'chevron':
      // Chevron arrow (no shaft, just pointing shape)
      commands.push({ type: 'moveTo', x: cx - hw, y: cy - hh });
      commands.push({ type: 'lineTo', x: cx + hw, y: cy });
      commands.push({ type: 'lineTo', x: cx - hw, y: cy + hh });
      commands.push({ type: 'lineTo', x: cx - hw + headWidth * 0.3, y: cy });
      commands.push({ type: 'closePath' });
      break;

    case 'pointer':
      // Simple triangle pointer
      commands.push({ type: 'moveTo', x: cx - hw, y: cy - hh });
      commands.push({ type: 'lineTo', x: cx + hw, y: cy });
      commands.push({ type: 'lineTo', x: cx - hw, y: cy + hh });
      commands.push({ type: 'closePath' });
      break;

    case 'double':
      // Double-headed arrow
      const midHead = headWidth * 0.7;
      commands.push({ type: 'moveTo', x: cx - hw, y: cy });
      commands.push({ type: 'lineTo', x: cx - hw + midHead, y: cy - hh });
      commands.push({ type: 'lineTo', x: cx - hw + midHead, y: cy - shaftHalfWidth });
      commands.push({ type: 'lineTo', x: cx + hw - midHead, y: cy - shaftHalfWidth });
      commands.push({ type: 'lineTo', x: cx + hw - midHead, y: cy - hh });
      commands.push({ type: 'lineTo', x: cx + hw, y: cy });
      commands.push({ type: 'lineTo', x: cx + hw - midHead, y: cy + hh });
      commands.push({ type: 'lineTo', x: cx + hw - midHead, y: cy + shaftHalfWidth });
      commands.push({ type: 'lineTo', x: cx - hw + midHead, y: cy + shaftHalfWidth });
      commands.push({ type: 'lineTo', x: cx - hw + midHead, y: cy + hh });
      commands.push({ type: 'closePath' });
      break;

    case 'curved':
      // Curved arrow with Bezier
      const curveOffset = height * 0.3;
      commands.push({ type: 'moveTo', x: cx - hw, y: cy + curveOffset - shaftHalfWidth });
      commands.push({
        type: 'bezierCurveTo',
        cp1x: cx, cp1y: cy - curveOffset - shaftHalfWidth,
        cp2x: cx + headStart, cp2y: cy - shaftHalfWidth,
        x: cx + headStart, y: cy - shaftHalfWidth
      });
      commands.push({ type: 'lineTo', x: cx + headStart, y: cy - hh });
      commands.push({ type: 'lineTo', x: cx + hw, y: cy });
      commands.push({ type: 'lineTo', x: cx + headStart, y: cy + hh });
      commands.push({ type: 'lineTo', x: cx + headStart, y: cy + shaftHalfWidth });
      commands.push({
        type: 'bezierCurveTo',
        cp1x: cx + headStart, cp1y: cy + shaftHalfWidth,
        cp2x: cx, cp2y: cy - curveOffset + shaftHalfWidth,
        x: cx - hw, y: cy + curveOffset + shaftHalfWidth
      });
      commands.push({ type: 'closePath' });
      break;
  }

  return commands;
}

/**
 * Generates cross/plus path
 * @param shape Shape instance with cross params
 * @returns Path commands
 */
function generateCrossPath(shape: ShapeInstance): PathCommand[] {
  const params = shape.shapeParams.params as CrossParams;
  const { width, height, armRatio } = params;
  const cx = shape.x;
  const cy = shape.y;

  const hw = width / 2;
  const hh = height / 2;
  const armWidth = Math.min(width, height) * Math.max(0.1, Math.min(0.5, armRatio));
  const haw = armWidth / 2;

  const commands: PathCommand[] = [];

  // 12-point cross shape, starting from top-left of top arm
  commands.push({ type: 'moveTo', x: cx - haw, y: cy - hh });
  commands.push({ type: 'lineTo', x: cx + haw, y: cy - hh });
  commands.push({ type: 'lineTo', x: cx + haw, y: cy - haw });
  commands.push({ type: 'lineTo', x: cx + hw, y: cy - haw });
  commands.push({ type: 'lineTo', x: cx + hw, y: cy + haw });
  commands.push({ type: 'lineTo', x: cx + haw, y: cy + haw });
  commands.push({ type: 'lineTo', x: cx + haw, y: cy + hh });
  commands.push({ type: 'lineTo', x: cx - haw, y: cy + hh });
  commands.push({ type: 'lineTo', x: cx - haw, y: cy + haw });
  commands.push({ type: 'lineTo', x: cx - hw, y: cy + haw });
  commands.push({ type: 'lineTo', x: cx - hw, y: cy - haw });
  commands.push({ type: 'lineTo', x: cx - haw, y: cy - haw });
  commands.push({ type: 'closePath' });

  return commands;
}

/**
 * Generates diamond path
 * @param shape Shape instance with diamond params
 * @returns Path commands
 */
function generateDiamondPath(shape: ShapeInstance): PathCommand[] {
  const params = shape.shapeParams.params as DiamondParams;
  const { width, height } = params;
  const cx = shape.x;
  const cy = shape.y;

  const hw = width / 2;
  const hh = height / 2;

  const commands: PathCommand[] = [];

  commands.push({ type: 'moveTo', x: cx, y: cy - hh }); // Top
  commands.push({ type: 'lineTo', x: cx + hw, y: cy }); // Right
  commands.push({ type: 'lineTo', x: cx, y: cy + hh }); // Bottom
  commands.push({ type: 'lineTo', x: cx - hw, y: cy }); // Left
  commands.push({ type: 'closePath' });

  return commands;
}

/**
 * Generates callout/speech bubble path
 * @param shape Shape instance with callout params
 * @returns Path commands
 */
function generateCalloutPath(shape: ShapeInstance): PathCommand[] {
  const params = shape.shapeParams.params as CalloutParams;
  const { width, height, cornerRadius, pointerPosition, pointerWidth, pointerLength, pointerOffset } = params;
  const cx = shape.x;
  const cy = shape.y;

  const hw = width / 2;
  const hh = height / 2;
  const r = Math.min(cornerRadius, Math.min(hw, hh));
  const pw = pointerWidth / 2;

  const commands: PathCommand[] = [];

  // Helper to add pointer on an edge
  const addPointer = (
    edgeStart: Point,
    edgeEnd: Point,
    direction: { x: number; y: number }
  ): void => {
    const edgeVec = { x: edgeEnd.x - edgeStart.x, y: edgeEnd.y - edgeStart.y };
    const edgeLen = Math.sqrt(edgeVec.x * edgeVec.x + edgeVec.y * edgeVec.y);
    const edgeNorm = { x: edgeVec.x / edgeLen, y: edgeVec.y / edgeLen };

    // Pointer base center position along edge
    const basePos = 0.5 + pointerOffset;
    const baseX = edgeStart.x + edgeVec.x * basePos;
    const baseY = edgeStart.y + edgeVec.y * basePos;

    // Pointer base points
    const base1 = { x: baseX - edgeNorm.x * pw, y: baseY - edgeNorm.y * pw };
    const base2 = { x: baseX + edgeNorm.x * pw, y: baseY + edgeNorm.y * pw };

    // Pointer tip
    const tip = {
      x: baseX + direction.x * pointerLength,
      y: baseY + direction.y * pointerLength
    };

    commands.push({ type: 'lineTo', x: base1.x, y: base1.y });
    commands.push({ type: 'lineTo', x: tip.x, y: tip.y });
    commands.push({ type: 'lineTo', x: base2.x, y: base2.y });
  };

  // Determine which edge has the pointer
  const hasPointer = (pos: string): boolean => {
    if (pointerPosition === pos) return true;
    if (pointerPosition === 'top-left' && (pos === 'top' || pos === 'left')) return false;
    if (pointerPosition === 'top-right' && (pos === 'top' || pos === 'right')) return false;
    if (pointerPosition === 'bottom-left' && (pos === 'bottom' || pos === 'left')) return false;
    if (pointerPosition === 'bottom-right' && (pos === 'bottom' || pos === 'right')) return false;
    return false;
  };

  // Build callout path (rectangle with one edge having pointer)
  // Start from top-left corner
  commands.push({ type: 'moveTo', x: cx - hw + r, y: cy - hh });

  // Top edge
  if (pointerPosition === 'top') {
    addPointer(
      { x: cx - hw + r, y: cy - hh },
      { x: cx + hw - r, y: cy - hh },
      { x: 0, y: -1 }
    );
  }
  commands.push({ type: 'lineTo', x: cx + hw - r, y: cy - hh });

  // Top-right corner
  if (r > 0) {
    commands.push({
      type: 'quadraticCurveTo',
      cpx: cx + hw, cpy: cy - hh,
      x: cx + hw, y: cy - hh + r
    });
  }

  // Right edge
  if (pointerPosition === 'right') {
    addPointer(
      { x: cx + hw, y: cy - hh + r },
      { x: cx + hw, y: cy + hh - r },
      { x: 1, y: 0 }
    );
  }
  commands.push({ type: 'lineTo', x: cx + hw, y: cy + hh - r });

  // Bottom-right corner
  if (r > 0) {
    commands.push({
      type: 'quadraticCurveTo',
      cpx: cx + hw, cpy: cy + hh,
      x: cx + hw - r, y: cy + hh
    });
  }

  // Bottom edge
  if (pointerPosition === 'bottom') {
    addPointer(
      { x: cx + hw - r, y: cy + hh },
      { x: cx - hw + r, y: cy + hh },
      { x: 0, y: 1 }
    );
  }
  commands.push({ type: 'lineTo', x: cx - hw + r, y: cy + hh });

  // Bottom-left corner
  if (r > 0) {
    commands.push({
      type: 'quadraticCurveTo',
      cpx: cx - hw, cpy: cy + hh,
      x: cx - hw, y: cy + hh - r
    });
  }

  // Left edge
  if (pointerPosition === 'left') {
    addPointer(
      { x: cx - hw, y: cy + hh - r },
      { x: cx - hw, y: cy - hh + r },
      { x: -1, y: 0 }
    );
  }
  commands.push({ type: 'lineTo', x: cx - hw, y: cy - hh + r });

  // Top-left corner
  if (r > 0) {
    commands.push({
      type: 'quadraticCurveTo',
      cpx: cx - hw, cpy: cy - hh,
      x: cx - hw + r, y: cy - hh
    });
  }

  commands.push({ type: 'closePath' });

  return commands;
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Calculates the bounding box of a shape
 * @param shape Shape instance
 * @returns Bounding box coordinates
 */
export function calculateShapeBounds(shape: ShapeInstance): {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
  width: number;
  height: number;
} {
  const commands = generateShapePath(shape);

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  for (const cmd of commands) {
    switch (cmd.type) {
      case 'moveTo':
      case 'lineTo':
        minX = Math.min(minX, cmd.x);
        minY = Math.min(minY, cmd.y);
        maxX = Math.max(maxX, cmd.x);
        maxY = Math.max(maxY, cmd.y);
        break;
      case 'bezierCurveTo':
        minX = Math.min(minX, cmd.cp1x, cmd.cp2x, cmd.x);
        minY = Math.min(minY, cmd.cp1y, cmd.cp2y, cmd.y);
        maxX = Math.max(maxX, cmd.cp1x, cmd.cp2x, cmd.x);
        maxY = Math.max(maxY, cmd.cp1y, cmd.cp2y, cmd.y);
        break;
      case 'quadraticCurveTo':
        minX = Math.min(minX, cmd.cpx, cmd.x);
        minY = Math.min(minY, cmd.cpy, cmd.y);
        maxX = Math.max(maxX, cmd.cpx, cmd.x);
        maxY = Math.max(maxY, cmd.cpy, cmd.y);
        break;
    }
  }

  // Apply rotation and scale
  if (shape.rotation !== 0 || shape.scale !== 1) {
    const corners = [
      { x: minX, y: minY },
      { x: maxX, y: minY },
      { x: maxX, y: maxY },
      { x: minX, y: maxY }
    ];

    const rad = (shape.rotation * Math.PI) / 180;
    const cos = Math.cos(rad);
    const sin = Math.sin(rad);

    minX = Infinity;
    minY = Infinity;
    maxX = -Infinity;
    maxY = -Infinity;

    for (const corner of corners) {
      // Translate to origin, scale, rotate, translate back
      const dx = (corner.x - shape.x) * shape.scale;
      const dy = (corner.y - shape.y) * shape.scale;
      const rx = dx * cos - dy * sin + shape.x;
      const ry = dx * sin + dy * cos + shape.y;

      minX = Math.min(minX, rx);
      minY = Math.min(minY, ry);
      maxX = Math.max(maxX, rx);
      maxY = Math.max(maxY, ry);
    }
  }

  return {
    minX,
    minY,
    maxX,
    maxY,
    width: maxX - minX,
    height: maxY - minY
  };
}

/**
 * Creates default parameters for a shape type
 * @param shapeType The type of shape
 * @param width Default width
 * @param height Default height
 * @returns Shape parameters
 */
export function createDefaultShapeParams(
  shapeType: ShapeType,
  width: number,
  height: number,
  settings?: ShapeToolSettings
): ShapeParams {
  switch (shapeType) {
    case 'rectangle':
      return {
        type: 'rectangle',
        params: {
          width,
          height,
          cornerRadius: { ...DEFAULT_CORNER_RADIUS, topLeft: settings?.rectangleCornerRadius ?? 0, topRight: settings?.rectangleCornerRadius ?? 0, bottomRight: settings?.rectangleCornerRadius ?? 0, bottomLeft: settings?.rectangleCornerRadius ?? 0 }
        }
      };
    case 'ellipse':
      return {
        type: 'ellipse',
        params: {
          radiusX: width / 2,
          radiusY: height / 2
        }
      };
    case 'polygon':
      return {
        type: 'polygon',
        params: {
          sides: settings?.polygonSides ?? 6,
          radius: Math.min(width, height) / 2,
          cornerRadius: 0
        }
      };
    case 'star':
      return {
        type: 'star',
        params: {
          points: settings?.starPoints ?? 5,
          outerRadius: Math.min(width, height) / 2,
          innerRatio: settings?.starInnerRatio ?? 0.5,
          cornerRadius: 0
        }
      };
    case 'arrow':
      return {
        type: 'arrow',
        params: {
          style: settings?.arrowStyle ?? 'block',
          width,
          height,
          headRatio: 0.4,
          shaftRatio: 0.5
        }
      };
    case 'cross':
      return {
        type: 'cross',
        params: {
          width,
          height,
          armRatio: 0.3
        }
      };
    case 'diamond':
      return {
        type: 'diamond',
        params: {
          width,
          height
        }
      };
    case 'callout':
      return {
        type: 'callout',
        params: {
          width,
          height,
          cornerRadius: 8,
          pointerPosition: settings?.calloutPointerPosition ?? 'bottom',
          pointerWidth: 20,
          pointerLength: 15,
          pointerOffset: 0
        }
      };
  }
}

/**
 * Gets the corner radius handle positions for a rectangle
 * Used for Illustrator-style live corner editing
 * @param shape Shape instance with rectangle params
 * @returns Array of corner handle data
 */
export function getRectangleCornerHandles(shape: ShapeInstance): Array<{
  corner: 'topLeft' | 'topRight' | 'bottomRight' | 'bottomLeft';
  x: number;
  y: number;
  radius: number;
  maxRadius: number;
}> {
  if (shape.shapeType !== 'rectangle') return [];

  const params = shape.shapeParams.params as RectangleParams;
  const { width, height, cornerRadius } = params;
  const hw = width / 2;
  const hh = height / 2;
  const maxR = Math.min(hw, hh);

  const getRadius = (corner: 'topLeft' | 'topRight' | 'bottomRight' | 'bottomLeft'): number => {
    if (cornerRadius.uniform) return Math.min(cornerRadius.topLeft, maxR);
    return Math.min(cornerRadius[corner], maxR);
  };

  // Handle positions are offset from corners along the diagonal
  const handleOffset = 0.7071; // 1/sqrt(2) for 45-degree diagonal

  return [
    {
      corner: 'topLeft',
      x: shape.x - hw + getRadius('topLeft') * handleOffset,
      y: shape.y - hh + getRadius('topLeft') * handleOffset,
      radius: getRadius('topLeft'),
      maxRadius: maxR
    },
    {
      corner: 'topRight',
      x: shape.x + hw - getRadius('topRight') * handleOffset,
      y: shape.y - hh + getRadius('topRight') * handleOffset,
      radius: getRadius('topRight'),
      maxRadius: maxR
    },
    {
      corner: 'bottomRight',
      x: shape.x + hw - getRadius('bottomRight') * handleOffset,
      y: shape.y + hh - getRadius('bottomRight') * handleOffset,
      radius: getRadius('bottomRight'),
      maxRadius: maxR
    },
    {
      corner: 'bottomLeft',
      x: shape.x - hw + getRadius('bottomLeft') * handleOffset,
      y: shape.y + hh - getRadius('bottomLeft') * handleOffset,
      radius: getRadius('bottomLeft'),
      maxRadius: maxR
    }
  ];
}

/**
 * Gets the inner radius handle position for a star
 * @param shape Shape instance with star params
 * @returns Handle position and data
 */
export function getStarInnerRadiusHandle(shape: ShapeInstance): {
  x: number;
  y: number;
  innerRatio: number;
  minRatio: number;
  maxRatio: number;
} | null {
  if (shape.shapeType !== 'star') return null;

  const params = shape.shapeParams.params as StarParams;
  const { points, outerRadius, innerRatio } = params;

  const innerRadius = outerRadius * innerRatio;

  // Handle position is at the first inner point (between first two outer points)
  const angle = -Math.PI / 2 + Math.PI / points;

  return {
    x: shape.x + innerRadius * Math.cos(angle),
    y: shape.y + innerRadius * Math.sin(angle),
    innerRatio,
    minRatio: 0.1,
    maxRatio: 0.9
  };
}

/**
 * Point-in-shape hit test using ray casting algorithm
 * @param shape Shape instance
 * @param testX Test point X
 * @param testY Test point Y
 * @returns True if point is inside the shape
 */
export function isPointInShape(shape: ShapeInstance, testX: number, testY: number): boolean {
  const commands = generateShapePath(shape);

  // Convert commands to polygon points for ray casting
  const points: Point[] = [];
  let currentX = 0;
  let currentY = 0;

  for (const cmd of commands) {
    switch (cmd.type) {
      case 'moveTo':
      case 'lineTo':
        points.push({ x: cmd.x, y: cmd.y });
        currentX = cmd.x;
        currentY = cmd.y;
        break;
      case 'bezierCurveTo':
        // Approximate bezier with line segments
        for (let t = 0.2; t <= 1; t += 0.2) {
          const mt = 1 - t;
          const mt2 = mt * mt;
          const mt3 = mt2 * mt;
          const t2 = t * t;
          const t3 = t2 * t;
          points.push({
            x: mt3 * currentX + 3 * mt2 * t * cmd.cp1x + 3 * mt * t2 * cmd.cp2x + t3 * cmd.x,
            y: mt3 * currentY + 3 * mt2 * t * cmd.cp1y + 3 * mt * t2 * cmd.cp2y + t3 * cmd.y
          });
        }
        currentX = cmd.x;
        currentY = cmd.y;
        break;
      case 'quadraticCurveTo':
        // Approximate quadratic with line segments
        for (let t = 0.25; t <= 1; t += 0.25) {
          const mt = 1 - t;
          points.push({
            x: mt * mt * currentX + 2 * mt * t * cmd.cpx + t * t * cmd.x,
            y: mt * mt * currentY + 2 * mt * t * cmd.cpy + t * t * cmd.y
          });
        }
        currentX = cmd.x;
        currentY = cmd.y;
        break;
    }
  }

  // Apply transformation to test point (inverse transform)
  let tx = testX;
  let ty = testY;

  if (shape.rotation !== 0 || shape.scale !== 1) {
    const rad = (-shape.rotation * Math.PI) / 180;
    const cos = Math.cos(rad);
    const sin = Math.sin(rad);

    const dx = testX - shape.x;
    const dy = testY - shape.y;

    tx = (dx * cos - dy * sin) / shape.scale + shape.x;
    ty = (dx * sin + dy * cos) / shape.scale + shape.y;
  }

  // Ray casting algorithm
  let inside = false;
  const n = points.length;

  for (let i = 0, j = n - 1; i < n; j = i++) {
    const xi = points[i].x;
    const yi = points[i].y;
    const xj = points[j].x;
    const yj = points[j].y;

    if (((yi > ty) !== (yj > ty)) && (tx < ((xj - xi) * (ty - yi)) / (yj - yi) + xi)) {
      inside = !inside;
    }
  }

  return inside;
}

/**
 * Distance from point to shape stroke
 * @param shape Shape instance
 * @param testX Test point X
 * @param testY Test point Y
 * @returns Distance to nearest stroke edge
 */
export function distanceToShapeStroke(shape: ShapeInstance, testX: number, testY: number): number {
  const commands = generateShapePath(shape);

  // Apply inverse transformation to test point
  let tx = testX;
  let ty = testY;

  if (shape.rotation !== 0 || shape.scale !== 1) {
    const rad = (-shape.rotation * Math.PI) / 180;
    const cos = Math.cos(rad);
    const sin = Math.sin(rad);

    const dx = testX - shape.x;
    const dy = testY - shape.y;

    tx = (dx * cos - dy * sin) / shape.scale + shape.x;
    ty = (dx * sin + dy * cos) / shape.scale + shape.y;
  }

  let minDist = Infinity;
  let prevX = 0;
  let prevY = 0;
  let startX = 0;
  let startY = 0;

  for (const cmd of commands) {
    switch (cmd.type) {
      case 'moveTo':
        prevX = cmd.x;
        prevY = cmd.y;
        startX = cmd.x;
        startY = cmd.y;
        break;
      case 'lineTo':
        minDist = Math.min(minDist, distToSegment(tx, ty, prevX, prevY, cmd.x, cmd.y));
        prevX = cmd.x;
        prevY = cmd.y;
        break;
      case 'bezierCurveTo':
        // Check distance to bezier approximation
        let bx = prevX;
        let by = prevY;
        for (let t = 0.1; t <= 1; t += 0.1) {
          const mt = 1 - t;
          const mt2 = mt * mt;
          const mt3 = mt2 * mt;
          const t2 = t * t;
          const t3 = t2 * t;
          const nx = mt3 * prevX + 3 * mt2 * t * cmd.cp1x + 3 * mt * t2 * cmd.cp2x + t3 * cmd.x;
          const ny = mt3 * prevY + 3 * mt2 * t * cmd.cp1y + 3 * mt * t2 * cmd.cp2y + t3 * cmd.y;
          minDist = Math.min(minDist, distToSegment(tx, ty, bx, by, nx, ny));
          bx = nx;
          by = ny;
        }
        prevX = cmd.x;
        prevY = cmd.y;
        break;
      case 'quadraticCurveTo':
        let qx = prevX;
        let qy = prevY;
        for (let t = 0.2; t <= 1; t += 0.2) {
          const mt = 1 - t;
          const nx = mt * mt * prevX + 2 * mt * t * cmd.cpx + t * t * cmd.x;
          const ny = mt * mt * prevY + 2 * mt * t * cmd.cpy + t * t * cmd.y;
          minDist = Math.min(minDist, distToSegment(tx, ty, qx, qy, nx, ny));
          qx = nx;
          qy = ny;
        }
        prevX = cmd.x;
        prevY = cmd.y;
        break;
      case 'closePath':
        minDist = Math.min(minDist, distToSegment(tx, ty, prevX, prevY, startX, startY));
        prevX = startX;
        prevY = startY;
        break;
    }
  }

  return minDist * shape.scale;
}

/**
 * Helper: distance from point to line segment
 */
function distToSegment(px: number, py: number, x1: number, y1: number, x2: number, y2: number): number {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const lengthSq = dx * dx + dy * dy;

  if (lengthSq === 0) {
    return Math.sqrt((px - x1) * (px - x1) + (py - y1) * (py - y1));
  }

  let t = ((px - x1) * dx + (py - y1) * dy) / lengthSq;
  t = Math.max(0, Math.min(1, t));

  const projX = x1 + t * dx;
  const projY = y1 + t * dy;

  return Math.sqrt((px - projX) * (px - projX) + (py - projY) * (py - projY));
}
